import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './AuthContext'
import { getApiBaseUrl } from '@/lib/api'

interface TeamData {
	team_id: string
	role: string
	id: string
	name: string
	slug: string
	plan_id: string
	created_at: string
	updated_at: string
}

interface TeamContextType {
	teamId: string | null
	role: string | null
	teamData: TeamData | null
	isLoading: boolean
	error: Error | null
	refetch: () => Promise<any>
	isInitialized: boolean
	setCurrentTeam: (teamId: string) => void
}

const TeamContext = createContext<TeamContextType | undefined>(undefined)

const getAuthHeaders = async () => {
	const {
		data: { session },
	} = await supabase.auth.getSession()
	return {
		Authorization: `Bearer ${session?.access_token}`,
		'Content-Type': 'application/json',
	}
}

export function TeamProvider({ children }: { children: ReactNode }) {
	const { session, isInitialized: authInitialized } = useAuth()
	const queryClient = useQueryClient()
	const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)

	// Set the current team for API context
	const setCurrentTeam = useCallback((teamId: string) => {
		setCurrentTeamId(teamId)
		queryClient.invalidateQueries({ queryKey: ['team', teamId] })
	}, [queryClient])

	const {
		data: teamData,
		isLoading,
		error,
		refetch,
	} = useQuery<TeamData | null, Error>({
		queryKey: ['team', currentTeamId],
		queryFn: async () => {
			if (!session) return null

			// If we have a currentTeamId, fetch that team's data
			if (currentTeamId) {
				const headers = await getAuthHeaders()
				const response = await fetch(`${getApiBaseUrl()}/api/team?team_id=${currentTeamId}`, {
					headers,
				})

				if (!response.ok) {
					let errorJson = { error: 'Failed to fetch team data' }
					try {
						errorJson = await response.json()
					} catch (e) {
						// Ignore if parsing fails
					}
					throw new Error(errorJson.error || 'Failed to fetch team data')
				}

				const text = await response.text()
				if (!text) return null

				return JSON.parse(text)
			}

			// No team selected, get default team
			const headers = await getAuthHeaders()
			const response = await fetch(`${getApiBaseUrl()}/api/team`, {
				headers,
			})

			if (!response.ok) {
				let errorJson = { error: 'Failed to fetch team data' }
				try {
					errorJson = await response.json()
				} catch (e) {
					// Ignore if parsing fails
				}

				// Handle "no team" case gracefully
				if (
					response.status === 403 &&
					errorJson.error === 'User is not part of any team'
				) {
					return null
				}
				throw new Error(errorJson.error || 'Failed to fetch team data')
			}

			const text = await response.text()
			if (!text) return null

			return JSON.parse(text)
		},
		enabled: authInitialized && !!session,
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
		refetchOnWindowFocus: false,
		retry: (failureCount, error: any) => {
			// Don't retry if user has no team
			if (error?.message?.includes('User is not part of any team')) {
				return false
			}
			return failureCount < 2
		},
	})

	const contextValue: TeamContextType = {
		teamId: teamData?.team_id ?? null,
		role: teamData?.role ?? null,
		teamData: teamData,
		isLoading: !authInitialized || isLoading,
		error: error,
		refetch,
		isInitialized: authInitialized && !isLoading,
		setCurrentTeam,
	}

	return (
		<TeamContext.Provider value={contextValue}>{children}</TeamContext.Provider>
	)
}

export function useTeamContext() {
	const context = useContext(TeamContext)
	if (context === undefined) {
		throw new Error('useTeamContext must be used within a TeamProvider')
	}
	return context
}

// Backward compatibility hook
export function useTeam() {
	const context = useTeamContext()
	return {
		teamId: context.teamId,
		role: context.role,
		teamData: context.teamData,
		isLoading: context.isLoading,
		error: context.error,
		refetch: context.refetch,
	}
}
